import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import {
  createSlaPolicy,
  deleteSlaPolicy,
  isApiError,
  listSlaPolicies,
  updateSlaPolicy,
} from "../../lib/support-api";
import type { SlaPolicy, SlaPolicyInput, TicketPriority } from "../../lib/support-types";
import { useConfig } from "../../state/config-context";

interface FormState {
  id: string | null;
  name: string;
  isActive: boolean;
  matchPriority: "" | TicketPriority;
  firstResponseMinutes: string;
  resolutionMinutes: string;
  escalateAfterBreach: boolean;
  escalationPriority: "" | "medium" | "high" | "urgent";
  sortOrder: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: "",
  isActive: true,
  matchPriority: "",
  firstResponseMinutes: "",
  resolutionMinutes: "",
  escalateAfterBreach: false,
  escalationPriority: "",
  sortOrder: "",
};

function SupportSlaInner(): ReactNode {
  const { t } = useConfig();
  const [items, setItems] = useState<SlaPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);


  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await listSlaPolicies();
      setItems(page.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (form === null) {
      return;
    }
    setSaving(true);
    setError(null);
    const firstResponse = Number.parseInt(form.firstResponseMinutes, 10);
    const resolution = Number.parseInt(form.resolutionMinutes, 10);
    const parsedOrder = Number.parseInt(form.sortOrder, 10);
    const input: SlaPolicyInput = {
      name: form.name.trim(),
      is_active: form.isActive,
      match_priority: form.matchPriority === "" ? null : form.matchPriority,
      first_response_minutes: firstResponse,
      resolution_minutes: resolution,
      escalate_after_breach: form.escalateAfterBreach,
      escalation_priority: form.escalationPriority === "" ? null : form.escalationPriority,
      sort_order: Number.isNaN(parsedOrder) ? undefined : parsedOrder,
    };
    try {
      if (form.id === null) {
        await createSlaPolicy(input);
      } else {
        await updateSlaPolicy(form.id, input);
      }
      setForm(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await deleteSlaPolicy(id);
      setConfirmId(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="page" aria-labelledby="support-sla-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="support-sla-title">{t("support.sla.title")}</h1>
          <p className="muted">{t("support.sla.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setForm({ ...EMPTY_FORM })}
        >
          {t("support.sla.new")}
        </button>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("support.sla.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("support.sla.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("support.sla.name")}</th>
                <th scope="col">{t("support.sla.matchPriority")}</th>
                <th scope="col">{t("support.sla.firstResponseMinutes")}</th>
                <th scope="col">{t("support.sla.resolutionMinutes")}</th>
                <th scope="col">{t("support.sla.isActive")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("support.common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((policy) => (
                <tr key={policy.id}>
                  <td>{policy.name}</td>
                  <td>
                    {policy.match_priority === null
                      ? t("support.common.all")
                      : policy.match_priority}
                  </td>
                  <td>{String(policy.first_response_minutes)}</td>
                  <td>{String(policy.resolution_minutes)}</td>
                  <td>
                    {policy.is_active ? t("support.sla.isActive") : t("support.common.none")}
                  </td>
                  <td className="table__actions-col">
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() =>
                          setForm({
                            id: policy.id,
                            name: policy.name,
                            isActive: policy.is_active,
                            matchPriority: (policy.match_priority ?? "") as FormState["matchPriority"],
                            firstResponseMinutes: String(policy.first_response_minutes),
                            resolutionMinutes: String(policy.resolution_minutes),
                            escalateAfterBreach: policy.escalate_after_breach,
                            escalationPriority: (policy.escalation_priority ??
                              "") as FormState["escalationPriority"],
                            sortOrder: String(policy.sort_order),
                          })
                        }
                      >
                        {t("common.edit")}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setConfirmId(policy.id)}
                      >
                        {t("support.common.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {form !== null ? (
        <Modal
          title={form.id === null ? t("support.sla.create") : t("support.sla.edit")}
          onClose={() => setForm(null)}
          busy={saving}
        >
          <form onSubmit={(event) => void onSubmit(event)}>
            <div className="form-grid">
              <label className="field" htmlFor="sla-name">
                <span>{t("support.sla.name")}</span>
                <input
                  id="sla-name"
                  value={form.name}
                  maxLength={120}
                  required
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="sla-match-priority">
                <span>{t("support.sla.matchPriority")}</span>
                <select
                  id="sla-match-priority"
                  value={form.matchPriority}
                  onChange={(event) =>
                    setForm({ ...form, matchPriority: event.target.value as FormState["matchPriority"] })
                  }
                >
                  <option value="">{t("support.common.all")}</option>
                  <option value="low">{t("support.priority.low")}</option>
                  <option value="medium">{t("support.priority.medium")}</option>
                  <option value="high">{t("support.priority.high")}</option>
                  <option value="urgent">{t("support.priority.urgent")}</option>
                </select>
              </label>
              <label className="field" htmlFor="sla-first-response">
                <span>{t("support.sla.firstResponseMinutes")}</span>
                <input
                  id="sla-first-response"
                  inputMode="numeric"
                  value={form.firstResponseMinutes}
                  required
                  min={1}
                  onChange={(event) => setForm({ ...form, firstResponseMinutes: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="sla-resolution">
                <span>{t("support.sla.resolutionMinutes")}</span>
                <input
                  id="sla-resolution"
                  inputMode="numeric"
                  value={form.resolutionMinutes}
                  required
                  min={1}
                  onChange={(event) => setForm({ ...form, resolutionMinutes: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="sla-escalate">
                <span>{t("support.sla.escalateAfterBreach")}</span>
                <input
                  id="sla-escalate"
                  type="checkbox"
                  checked={form.escalateAfterBreach}
                  onChange={(event) =>
                    setForm({ ...form, escalateAfterBreach: event.target.checked })
                  }
                />
              </label>
              <label className="field" htmlFor="sla-escalation-priority">
                <span>{t("support.sla.escalationPriority")}</span>
                <select
                  id="sla-escalation-priority"
                  value={form.escalationPriority}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      escalationPriority: event.target.value as FormState["escalationPriority"],
                    })
                  }
                >
                  <option value="">{t("support.common.none")}</option>
                  <option value="medium">{t("support.priority.medium")}</option>
                  <option value="high">{t("support.priority.high")}</option>
                  <option value="urgent">{t("support.priority.urgent")}</option>
                </select>
              </label>
              <label className="field" htmlFor="sla-order">
                <span>{t("support.sla.sortOrder")}</span>
                <input
                  id="sla-order"
                  inputMode="numeric"
                  value={form.sortOrder}
                  onChange={(event) => setForm({ ...form, sortOrder: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="sla-active">
                <span>{t("support.sla.isActive")}</span>
                <input
                  id="sla-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? t("common.saving") : t("common.save")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmId !== null ? (
        <Modal
          title={t("support.sla.deleteConfirm")}
          onClose={() => setConfirmId(null)}
          busy={saving}
          footer={
            <>
              <button
                type="button"
                className="btn btn--danger"
                disabled={saving}
                onClick={() => void onDelete(confirmId)}
              >
                {t("support.common.delete")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmId(null)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("support.sla.deleteConfirm")}</p>
        </Modal>
      ) : null}
    </section>
  );
}

export function SupportSlaPage(): ReactNode {
  return (
    <RequirePermission permission="support.sla.manage">
      <SupportSlaInner />
    </RequirePermission>
  );
}